/* eslint-disable @typescript-eslint/naming-convention */
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { lastValueFrom } from 'rxjs';

import { authConfig, jhtAuthEndpoints } from '../../../auth.config';
import { StorageService } from './storage/storage.service';

export interface AuthTokenResponse {
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
}

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRES_AT_KEY = 'access_token_expires_at';

// Refresh slightly before the token actually expires to avoid racing the server clock
const EXPIRY_LEEWAY_MS = 60 * 1000;

/**
 * Holds the Auth0 access/refresh tokens used to call BAPI.
 *
 * Tokens are only needed while online: when offline, data is read from the
 * local cache, so an expired access token never blocks the app. It is
 * refreshed the next time a BAPI request is made with a connection.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
    private http = inject(HttpClient);
    private storage = inject(StorageService);
    private refreshInFlight: Promise<string | null> | null = null;

    async setTokens(tokens: AuthTokenResponse): Promise<void> {
        const expiresAt = Date.now() + tokens.expires_in * 1000;

        await Promise.all([
            this.storage.set(ACCESS_TOKEN_KEY, tokens.access_token),
            this.storage.set(TOKEN_EXPIRES_AT_KEY, String(expiresAt)),
            // Auth0 only returns a refresh token when one is issued/rotated
            tokens.refresh_token
                ? this.storage.set(REFRESH_TOKEN_KEY, tokens.refresh_token)
                : Promise.resolve(),
        ]);
    }

    async clear(): Promise<void> {
        await Promise.all([
            this.storage.remove(ACCESS_TOKEN_KEY),
            this.storage.remove(REFRESH_TOKEN_KEY),
            this.storage.remove(TOKEN_EXPIRES_AT_KEY),
        ]);
    }

    /**
     * Returns a valid access token, refreshing it if expired.
     * Resolves to null when there is no session or the refresh fails (e.g. offline).
     */
    async getAccessToken(): Promise<string | null> {
        const [token, expiresAt] = await Promise.all([
            this.storage.get(ACCESS_TOKEN_KEY),
            this.storage.get(TOKEN_EXPIRES_AT_KEY).then((exp) => (exp ? +exp : 0)),
        ]);

        if (token && Date.now() < expiresAt - EXPIRY_LEEWAY_MS) {
            return token;
        }

        if (!this.refreshInFlight) {
            this.refreshInFlight = this.refresh().finally(() => (this.refreshInFlight = null));
        }
        return this.refreshInFlight;
    }

    /**
     * Whether tokens were stored at login. Works offline: it does not check expiry,
     * since an expired access token is refreshed once a connection is available.
     */
    async hasTokens(): Promise<boolean> {
        const [accessToken, refreshToken] = await Promise.all([
            this.storage.get(ACCESS_TOKEN_KEY),
            this.storage.get(REFRESH_TOKEN_KEY),
        ]);
        return !!(accessToken || refreshToken);
    }

    async getAuthHeaders(): Promise<Record<string, string>> {
        const token = await this.getAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    private async refresh(): Promise<string | null> {
        const refreshToken = await this.storage.get(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            return null;
        }

        try {
            const tokens = await lastValueFrom(
                this.http.post<AuthTokenResponse>(jhtAuthEndpoints.token, {
                    grant_type: 'refresh_token',
                    client_id: authConfig.clientId,
                    refresh_token: refreshToken,
                })
            );
            await this.setTokens(tokens);
            return tokens.access_token;
        } catch (e) {
            console.log('Access token refresh failed: ', e);
            return null;
        }
    }
}
